import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './ProfileSetup.module.css';
import { getProfile, updateProfile, uploadProfileImage } from '../../../apis/api'; // Adjust path
import profileFallback from '../../../assets/pic/dogeja_cat1.png'; // Use existing fallback
import iconCamera from '../../../assets/pic/채팅방/사진선택.png'; // Adjust path if needed
import { AiOutlineArrowRight } from 'react-icons/ai';

import Header from '../../../components/Header/Header';
import Loading from '../../../components/Loading/Loading';
import { clearAccessToken } from '../../../utils/authToken';

const ProfileSetup = () => {
    const navigate = useNavigate();
    const [nickname, setNickname] = useState('');
    const [birthDate, setBirthDate] = useState({ year: '', month: '', day: '' });
    const [profileImageUrl, setProfileImageUrl] = useState(null);
    const [loading, setLoading] = useState(true);
    const [accountUsername, setAccountUsername] = useState('');
    const fileInputRef = useRef(null);

    // Fetch existing profile (e.g. username as default nickname)
    useEffect(() => {
        const fetchUserData = async () => {
            try {
                const data = await getProfile();
                if (data) {
                    setNickname(data.nickname || data.username || '');
                    if (data.username) setAccountUsername(data.username);
                    if (data.profileImageUrl) {
                        setProfileImageUrl(data.profileImageUrl);
                    }
                    if (data.birthDate) {
                        const parts = data.birthDate.split('-');
                        if (parts.length === 3) {
                            setBirthDate({ year: parts[0], month: parts[1], day: parts[2] });
                        }
                    }
                }
            } catch (error) {
                console.error("Failed to fetch profile", error);
                if (error.response && error.response.status === 401) {
                    alert("세션이 만료되었습니다.");
                    clearAccessToken();
                    navigate('/login');
                }
            } finally {
                setLoading(false);
            }
        };
        fetchUserData();
    }, [navigate]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'nickname') setNickname(value);
        else setBirthDate(prev => ({ ...prev, [name]: value }));
    };

    const handleImageClick = () => {
        fileInputRef.current.click();
    };

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Check file type
        const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
        if (!validTypes.includes(file.type)) {
            alert("허용되지 않는 파일 형식입니다. (jpg, png, webp 만 가능)");
            return;
        }

        try {
            const data = await uploadProfileImage(file);
            // Optimistic update
            if (data && data.profileImageUrl) {
                setProfileImageUrl(data.profileImageUrl);
            }
        } catch (error) {
            console.error("Failed to upload image", error);
            if (error.response) {
                if (error.response.status === 401) {
                    alert("세션이 만료되었습니다.");
                    clearAccessToken();
                    navigate('/login');
                } else if (error.response.status === 400) {
                    alert(error.response.data?.message || "이미지 업로드에 실패했습니다. (잘못된 요청)");
                } else {
                    alert("이미지 업로드 중 오류가 발생했습니다.");
                }
            }
        } finally {
            e.target.value = '';
        }
    };

    const handleSubmit = async () => {
        // Validation
        if (!nickname.trim()) {
            alert("닉네임을 입력해주세요.");
            return;
        }

        const { year, month, day } = birthDate;
        let formattedDate = null;

        if (year && month && day) {
            const y = parseInt(year);
            const m = parseInt(month);
            const d = parseInt(day);
            if (isNaN(y) || isNaN(m) || isNaN(d) || y < 1900 || m < 1 || m > 12 || d < 1 || d > 31) {
                alert("올바른 생년월일을 입력해주세요.");
                return;
            }
            formattedDate = `${y}-${m.toString().padStart(2, '0')}-${d.toString().padStart(2, '0')}`;
        } else {
            alert("생년월일을 모두 입력해주세요.");
            return;
        }

        try {
            await updateProfile({
                nickname: nickname,
                birthDate: formattedDate
            });
            // Mark onboarding tour as pending for first-time profile setup users
            localStorage.setItem('pendingOnboardingTour', 'true');
            if (accountUsername) {
                localStorage.setItem('pendingOnboardingTourUsername', accountUsername);
            }
            // Proceed to Home or next step
            alert("프로필 설정이 완료되었습니다!");
            navigate('/home');
        } catch (error) {
            console.error("Update failed", error);
            alert("설정에 실패했습니다. 다시 시도해주세요.");
        }
    };

    if (loading) return <Loading />;

    return (
        <div className={styles.container}>

            <div className={styles.topGroup}>
                <Header />

                <div className={styles.greeting}>
                    반가워요!<br />
                    톡플랜과 함께 하기 위해<br />
                    몇가지 정보를 입력해주세요.
                </div>
            </div>

            <div className={styles.sectionTitle} style={{ marginTop: '0' }}>
                프로필을 완성해주세요.
            </div>

            <div className={styles.profileSection}>
                <div className={styles.profileImageWrapper}>
                    <img src={profileImageUrl || profileFallback} alt="Profile" className={styles.profileImage} />
                    <img
                        src={iconCamera}
                        alt="Edit"
                        className={styles.cameraBadge}
                        onClick={handleImageClick}
                    />
                    <input
                        type="file"
                        ref={fileInputRef}
                        style={{ display: 'none' }}
                        accept="image/jpeg, image/png, image/webp"
                        onChange={handleFileChange}
                    />
                </div>

                <input
                    type="text"
                    name="nickname"
                    value={nickname}
                    onChange={handleChange}
                    className={styles.nicknameInput}
                    placeholder="닉네임"
                    maxLength={20}
                />
            </div>

            <div className={styles.helperText} style={{ textAlign: 'center', marginBottom: '30px' }}>
                대화방에서 보일 닉네임을 입력해주세요 <span className={styles.requiredMark}>*</span> {nickname.length}/20
            </div>


            <div className={styles.sectionTitle}>
                생년월일을 입력해주세요
            </div>
            <div className={styles.subText}>
                입력 정보를 기반으로 ai가 일정을 추천드릴게요!
            </div>

            <div className={styles.dateInputs}>
                <input
                    type="text"
                    name="year"
                    value={birthDate.year}
                    onChange={handleChange}
                    placeholder="YYYY"
                    className={`${styles.dateInput} ${styles.yearInput}`}
                    maxLength={4}
                />
                <input
                    type="text"
                    name="month"
                    value={birthDate.month}
                    onChange={handleChange}
                    placeholder="MM"
                    className={`${styles.dateInput} ${styles.monthInput}`}
                    maxLength={2}
                />
                <input
                    type="text"
                    name="day"
                    value={birthDate.day}
                    onChange={handleChange}
                    placeholder="DD"
                    className={`${styles.dateInput} ${styles.dayInput}`}
                    maxLength={2}
                />
            </div>

            <button className={styles.nextButton} onClick={handleSubmit}>
                다음
                <AiOutlineArrowRight className={styles.arrowIcon} />
            </button>
        </div>
    );
};

export default ProfileSetup;
